 
import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { DollarSign, Upload, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GroceryCashPage() {
  const [groceryCash, setGroceryCash] = useState([]);
  const [summary, setSummary] = useState({
    totalReceived: 0,
    totalStudents: 0,
    averageAmount: 0,
    byGrade: []
  });
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [formData, setFormData] = useState({
    studentId: '',
    termId: '',
    academicYear: '2024',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'Cash',
    notes: ''
  });

  const [filters, setFilters] = useState({
    term: '',
    grade: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  // Fetch grocery cash data
  useEffect(() => {
    const fetchGroceryCash = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API}/grocery-cash`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (!response.ok) throw new Error('Failed to fetch');
        
        const data = await response.json();
        setGroceryCash(data.transactions || []);
        setSummary({
          totalReceived: Number(data.totalCashReceived || 0),
          totalStudents: Number(data.totalTransactions || 0),
          averageAmount: Number(data.averagePerStudent || 0),
          byGrade: data.byGrade || []
        });
      } catch (error) {
        console.error('Error fetching grocery cash:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGroceryCash();
  }, []);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && ['application/pdf', 'image/png', 'image/jpeg'].includes(file.type)) {
      setSelectedFile(file);
    } else {
      alert('Please select a valid PDF or image file');
    }
  };

  // Handle add grocery cash
  const handleAddGroceryCash = async (e) => {
    e.preventDefault();
    
    if (!formData.studentId || !formData.amount || formData.amount <= 0) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const uploadPayload = new FormData();
      uploadPayload.append('studentId', formData.studentId);
      uploadPayload.append('termId', formData.termId);
      uploadPayload.append('academicYear', formData.academicYear || '2024');
      uploadPayload.append('amount', String(parseFloat(formData.amount)));
      uploadPayload.append('paymentDate', formData.paymentDate);
      uploadPayload.append('paymentMethod', formData.paymentMethod);
      uploadPayload.append('notes', formData.notes || '');
      if (selectedFile) {
        uploadPayload.append('receiptFile', selectedFile);
      }

      const response = await fetch(`${API}/grocery-cash`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: uploadPayload
      });

      if (response.ok) {
        const transactionData = await response.json();

        // Reset form and reload data
        setFormData({
          studentId: '',
          termId: '',
          academicYear: '2024',
          amount: '',
          paymentDate: new Date().toISOString().split('T')[0],
          paymentMethod: 'Cash',
          notes: ''
        });
        setSelectedFile(null);
        setShowAddForm(false);
        
        // Reload data
        const reloadResponse = await fetch(`${API}/grocery-cash`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const reloadData = await reloadResponse.json();
        setGroceryCash(reloadData.transactions || []);
        setSummary({
          totalReceived: Number(reloadData.totalCashReceived || 0),
          totalStudents: Number(reloadData.totalTransactions || 0),
          averageAmount: Number(reloadData.averagePerStudent || 0),
          byGrade: reloadData.byGrade || []
        });
      }
    } catch (error) {
      console.error('Error adding grocery cash:', error);
      alert('Failed to record grocery cash contribution');
    }
  };

  // Handle approve transaction
  const handleApprove = async (id) => {
    try {
      const response = await fetch(`${API}/grocery-cash/${id}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        // Reload data
        const reloadResponse = await fetch(`${API}/grocery-cash`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const reloadData = await reloadResponse.json();
        setGroceryCash(reloadData.transactions || []);
        setSummary(reloadData.summary || {});
      }
    } catch (error) {
      console.error('Error approving transaction:', error);
    }
  };

  // Filter transactions
  const filteredGroceryCash = groceryCash.filter(transaction => {
    if (filters.term && transaction.termId !== parseInt(filters.term)) return false;
    if (filters.grade && transaction.studentGrade !== filters.grade) return false;
    if (filters.status && transaction.status !== filters.status) return false;
    if (filters.dateFrom && new Date(transaction.paymentDate) < new Date(filters.dateFrom)) return false;
    if (filters.dateTo && new Date(transaction.paymentDate) > new Date(filters.dateTo)) return false;
    return true;
  });

  // Status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Approved': return 'bg-blue-100 text-blue-800';
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'Rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
      case 'Completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'Pending':
        return <Clock className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading grocery cash data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Grocery Cash Management</h1>
        <Button 
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-green-600 hover:bg-green-700"
        >
          {showAddForm ? 'Cancel' : '+ Record Cash Contribution'}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Cash Received</p>
              <p className="text-2xl font-bold text-green-700">${Number(summary.totalReceived || 0).toFixed(2)}</p>
            </div>
            <DollarSign className="w-10 h-10 text-green-600" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div>
            <p className="text-sm text-gray-600">Students Contributed</p>
            <p className="text-2xl font-bold text-blue-700">{summary.totalStudents || 0}</p>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div>
            <p className="text-sm text-gray-600">Average Contribution</p>
              <p className="text-2xl font-bold text-purple-700">${Number(summary.averageAmount || 0).toFixed(2)}</p>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <div>
            <p className="text-sm text-gray-600">Total Transactions</p>
            <p className="text-2xl font-bold text-indigo-700">{groceryCash.length}</p>
          </div>
        </Card>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <Card className="p-6 bg-blue-50 border-blue-200">
          <h3 className="text-lg font-semibold mb-4">Record Grocery Cash Contribution</h3>
          <form onSubmit={handleAddGroceryCash} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Student
                </label>
                <Select
                  name="studentId"
                  value={formData.studentId}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Student</option>
                  <option value="student-001">John Doe - Grade 4A</option>
                  <option value="student-002">Jane Smith - Grade 5B</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Term
                </label>
                <Select
                  name="termId"
                  value={formData.termId}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Term</option>
                  <option value="1">Term 1</option>
                  <option value="2">Term 2</option>
                  <option value="3">Term 3</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount ($)
                </label>
                <Input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Date
                </label>
                <Input
                  type="date"
                  name="paymentDate"
                  value={formData.paymentDate}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method
                </label>
                <Select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleInputChange}
                >
                  <option value="Cash">Cash</option>
                  <option value="Check">Check</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Receipt/Proof
                </label>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                />
                {selectedFile && (
                  <p className="text-sm text-green-600 mt-2">✓ {selectedFile.name}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Additional notes..."
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
              >
                Record Contribution
              </Button>
              <Button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="bg-gray-400 hover:bg-gray-500"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4 bg-gray-50">
        <button
          onClick={() => setShowFilter(!showFilter)}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
        >
          {showFilter ? '▼' : '▶'} Advanced Filters
        </button>

        {showFilter && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <Select
              value={filters.term}
              onChange={(e) => setFilters({...filters, term: e.target.value})}
            >
              <option value="">All Terms</option>
              <option value="1">Term 1</option>
              <option value="2">Term 2</option>
              <option value="3">Term 3</option>
            </Select>

            <Select
              value={filters.grade}
              onChange={(e) => setFilters({...filters, grade: e.target.value})}
            >
              <option value="">All Grades</option>
              <option value="Grade 1">Grade 1</option>
              <option value="Grade 2">Grade 2</option>
              <option value="Grade 7">Grade 7</option>
            </Select>

            <Select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
            >
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Completed">Completed</option>
            </Select>

            <Input
              type="date"
              placeholder="From Date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
            />

            <Input
              type="date"
              placeholder="To Date"
              value={filters.dateTo}
              onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
            />
          </div>
        )}
      </Card>

      {/* Transactions Table */}
      <Card className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-100 border-b-2 border-gray-300">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Student</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Grade</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Date Paid</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Receipt</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredGroceryCash.map((transaction) => (
              <tr key={transaction.id} className="border-b hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-900">{transaction.studentName}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{transaction.studentGrade}</td>
                <td className="px-6 py-4 text-sm font-medium text-green-700">
                  ${transaction.amount?.toFixed(2) || '0.00'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {new Date(transaction.paymentDate).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-sm">
                  {transaction.receipt_file_path ? (
                    <a href={transaction.receipt_file_path} className="text-blue-600 hover:underline">
                      View Receipt
                    </a>
                  ) : (
                    <span className="text-gray-400">No receipt</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${getStatusColor(transaction.status)}`}>
                    {getStatusIcon(transaction.status)}
                    {transaction.status}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm space-x-2">
                  {transaction.status === 'Pending' && (
                    <>
                      <Button
                        onClick={() => handleApprove(transaction.id)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        ✓ Approve
                      </Button>
                      <input
                        type="file"
                        id={`receipt-${transaction.id}`}
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const uploadFormData = new FormData();
                            uploadFormData.append('receiptFile', file);
                            fetch(`${API}/grocery-cash/${transaction.id}/upload-receipt`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                              body: uploadFormData
                            });
                          }
                        }}
                        className="hidden"
                      />
                      <label htmlFor={`receipt-${transaction.id}`}>
                        <Button as="span" size="sm" className="bg-blue-600 hover:bg-blue-700 cursor-pointer">
                          📄 Upload
                        </Button>
                      </label>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredGroceryCash.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No grocery cash transactions found
          </div>
        )}
      </Card>

      {/* Grade Breakdown */}
      {summary.byGrade && summary.byGrade.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Breakdown by Grade</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {summary.byGrade.map((grade) => (
              <Card key={grade.grade} className="p-4 bg-gray-50 border-l-4 border-blue-500">
                <p className="text-sm text-gray-600">{grade.grade}</p>
                <p className="text-xl font-bold text-gray-900">${grade.total?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-gray-500">{grade.count} students</p>
              </Card>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
